// The one-tap prompt on the Alarms page for a boat nobody has asked yet
// (ADR 0173). "Is this a power boat or a sailboat? This tunes which modes your
// alarms use."
//
// WHY IT IS HERE AND NOT IN AN FTUE STEP. The vessel type measured NULL on every
// boat in the recent fleet backups — the field existed for years and nothing
// ever asked. A setting nobody is asked for is a setting nobody has, so the
// question has to appear where an owner already is, on the page whose behaviour
// it changes. Asking during first-run setup is the follow-up; it does not help
// the boats already out there.
//
// It is a QUESTION, not a nag:
//   * it appears only while the answer is unknown AND the boat is online (the
//     only way to answer is a relay to the boat);
//   * answering makes it disappear — through the mirror, not through local
//     state, so it disappears because the BOAT now knows, not because we asked;
//   * "Not now" is a real answer too, remembered per boat in this browser.

import { useDemoGuest } from '../../auth';
import { useState } from 'react';

import {
  VESSEL_TYPE_DISMISS_LABEL,
  VESSEL_TYPE_PROMPT_QUESTION,
  VESSEL_TYPE_PROMPT_WHY,
  dismissVesselTypePrompt,
  isVesselTypePromptDismissed,
  shouldPromptForVesselType,
  type VesselType,
} from '../../lib/vesselType';
import { VesselTypeChoice } from '../boat/VesselTypeChoice';

interface VesselTypePromptCardProps {
  boatId: string;
  /** The mirror. Anything other than unknown and this card is not rendered. */
  vesselType: VesselType | null | undefined;
  boatOnline: boolean;
  /** Re-read the alarm state so the answer's echo lands without a reload. */
  onSent: () => void;
}

export function VesselTypePromptCard({
  boatId,
  vesselType,
  boatOnline,
  onSent,
}: VesselTypePromptCardProps) {
  // Read once per mount: a dismissal made in another tab should not make the
  // card vanish mid-tap here, and re-reading on every render would do exactly
  // that.
  const [dismissed, setDismissed] = useState(() => isVesselTypePromptDismissed(boatId));
  // A Try it guest cannot send the boat its vessel type (TOM-868).
  const guest = useDemoGuest();

  if (guest) return null;

  if (!shouldPromptForVesselType({ vesselType, boatOnline, dismissed })) return null;

  return (
    <section className="alarm-band vessel-type-prompt" data-testid="alarms-vessel-type-prompt">
      <div className="vessel-type-prompt__head">
        <h2 className="alarm-band__title">{VESSEL_TYPE_PROMPT_QUESTION}</h2>
        <p className="vessel-type-prompt__why">{VESSEL_TYPE_PROMPT_WHY}</p>
      </div>
      <VesselTypeChoice
        boatId={boatId}
        vesselType={vesselType}
        boatOnline={boatOnline}
        onSent={onSent}
        testIdPrefix="vessel-type-prompt"
      />
      <button
        type="button"
        className="vessel-type-prompt__dismiss"
        data-testid="vessel-type-prompt-dismiss"
        onClick={() => {
          dismissVesselTypePrompt(boatId);
          setDismissed(true);
        }}
      >
        {VESSEL_TYPE_DISMISS_LABEL}
      </button>
    </section>
  );
}
